import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useSearch } from '@tanstack/react-router';
import { AlertCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from '@/components/ui/card';
import { PasswordInput } from '@/components/ui/password-input';
import { Label } from '@/components/ui/label';
import { Alert, AlertDescription } from '@/components/ui/alert';
import {
  resetPasswordSchema,
  type ResetPasswordForm,
} from '@/lib/schemas/profile';
import { useSetInitialPassword } from '@/lib/api/profile';
import { ApiError } from '@/lib/api';

/**
 * /onboarding/set-password?next=… — «Придумайте пароль» при первом входе по
 * приглашению.
 *
 * После accept-invite новый user auto-залогинен (session-cookie), но пароля у
 * него ещё нет — повторный вход email+password был бы невозможен. Эта форма
 * обязательна (нет кнопки «позже»/skip): user задаёт пароль, вызывается
 * существующий POST /auth/set-password (по session-cookie), после чего мы
 * переходим на исходный target из ?next=… (напр. /onboarding/profile,
 * /dashboard или /map?promoted=1).
 *
 * Recovery: если user всё же покинул страницу не задав пароль — доступ
 * восстанавливается через «Забыли пароль» на /login (reset-flow работает и
 * для null password_hash).
 */
export function safeNext(raw: string | undefined): string {
  // Защита от open-redirect: резолвим через тот же WHATWG-парсер, что и
  // window.location.replace, и сверяем origin. Строковый startsWith('//') не
  // ловил backslash/tab-обходы (`/\/evil.com`, `/\tevil.com`) — браузер
  // нормализует их в cross-origin authority. Возвращаем только внутренний путь.
  if (!raw) return '/dashboard';
  try {
    const u = new URL(raw, window.location.origin);
    if (u.origin !== window.location.origin) return '/dashboard';
    return u.pathname + u.search;
  } catch {
    return '/dashboard';
  }
}

/**
 * AK-584 — куда идти после сохранения пароля.
 *
 * Приглашение без явной цели («просто зайди в систему») раньше высаживало
 * человека на пустой дашборд без единой подсказки. Теперь в этом случае
 * врезается мастер первого входа; он сам мгновенно уйдёт дальше, если поля в
 * тенанте уже есть (второй агроном того же клиента мастер не увидит).
 *
 * Явный `?next=` (например `/map?promoted=1`) уважаем как есть: человека звали
 * в конкретное место, вставать между ним и целью мастеру нечего.
 */
export function postPasswordTarget(raw: string | undefined): string {
  const next = safeNext(raw);
  if (raw && next !== '/dashboard') return next;
  return '/onboarding/first-run';
}

export function SetPasswordOnboardingPage() {
  const search = useSearch({ from: '/onboarding/set-password' }) as {
    next?: string;
  };
  const next = postPasswordTarget(search.next);
  const setPwMut = useSetInitialPassword();
  const [serverError, setServerError] = useState<string | null>(null);

  const {
    register,
    handleSubmit,
    formState: { errors, isSubmitting, isValid },
  } = useForm<ResetPasswordForm>({
    resolver: zodResolver(resetPasswordSchema),
    // AK-465 (добивка к PR #534): НЕ возвращать 'onBlur'. В mode:'onBlur' RHF
    // отбрасывает change-события (skipValidation) — валидация стартует только
    // на blur, `isValid` остаётся начальным false. Кнопка ниже =
    // `disabled={isSubmitting || !isValid}` → пока пользователь не увёл фокус
    // с «Повторите пароль» (Tab/клик по пустому месту), она заблокирована,
    // а Enter в поле / «Go» на мобильной клавиатуре не сабмитят вовсе —
    // браузер не диспатчит click на disabled default button (implicit
    // submission). Для ЭТОЙ формы цена максимальная: это первый вход
    // приглашённого пользователя, у него ещё нет пароля и обойти экран нечем.
    // 'onChange' валидирует на каждом keystroke → isValid поднимается сразу.
    mode: 'onChange',
    defaultValues: { new_password: '', confirm_password: '' },
  });

  const onSubmit = async (data: ResetPasswordForm) => {
    setServerError(null);
    try {
      await setPwMut.mutateAsync({ password: data.new_password });
      // Полная навигация (а не router.navigate) — гарантирует, что цель
      // (/map, /dashboard, /onboarding/profile) загрузится со свежими cookie.
      window.location.replace(next);
    } catch (err) {
      if (err instanceof ApiError) setServerError(err.detail);
      else setServerError('Сетевая ошибка');
    }
  };

  return (
    <div className="min-h-dvh flex items-center justify-center bg-secondary/40 p-6">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <CardTitle className="text-2xl font-semibold">
            Придумайте пароль
          </CardTitle>
          <CardDescription>
            Задайте пароль, чтобы входить в систему в следующий раз
          </CardDescription>
        </CardHeader>
        <CardContent>
          <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
            {serverError && (
              <Alert variant="destructive">
                <AlertCircle className="h-4 w-4" />
                <AlertDescription>{serverError}</AlertDescription>
              </Alert>
            )}

            <div className="space-y-1.5">
              <Label htmlFor="set-new-password">Пароль</Label>
              <PasswordInput
                id="set-new-password"
                autoComplete="new-password"
                aria-invalid={!!errors.new_password}
                className="h-10"
                {...register('new_password')}
              />
              {errors.new_password ? (
                <p className="text-destructive text-xs mt-1">
                  {errors.new_password.message}
                </p>
              ) : (
                <p className="text-muted-foreground text-xs mt-1">
                  Минимум 8 символов
                </p>
              )}
            </div>

            <div className="space-y-1.5">
              <Label htmlFor="set-confirm-password">Повторите пароль</Label>
              <PasswordInput
                id="set-confirm-password"
                autoComplete="new-password"
                aria-invalid={!!errors.confirm_password}
                className="h-10"
                {...register('confirm_password')}
              />
              {errors.confirm_password && (
                <p className="text-destructive text-xs mt-1">
                  {errors.confirm_password.message}
                </p>
              )}
            </div>

            <Button
              type="submit"
              disabled={isSubmitting || !isValid}
              className="w-full"
              size="lg"
            >
              {isSubmitting ? 'Сохраняем…' : 'Сохранить и продолжить'}
            </Button>
          </form>
        </CardContent>
      </Card>
    </div>
  );
}

export default SetPasswordOnboardingPage;
